import { useState, useEffect } from 'react';
import { API_BASE_URL, getOAuthStartUrl } from '../config/api';

interface Props {
  onConnected: (userId: string) => void;
}

interface CredentialsStatus {
  connected: boolean;
  accountId?: string;
  email?: string;
  expiresAt?: string;
}

export default function ConnectDocuSign({ onConnected }: Props) {
  const [status, setStatus] = useState<CredentialsStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = localStorage.getItem('docusign_user_id');

  useEffect(() => {
    if (userId) {
      checkStatus();
    }
  }, [userId]);

  const checkStatus = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/credentials?userId=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        if (data.connected) {
          onConnected(userId);
        }
      }
    } catch (err) {
      console.error('Failed to check status:', err);
      setError('Failed to check connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    window.location.href = getOAuthStartUrl();
  };

  const handleDisconnect = () => {
    localStorage.removeItem('docusign_user_id');
    setStatus(null);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="text-gray-500">
        <p>Checking connection status...</p>
      </div>
    );
  }

  if (status?.connected) {
    return (
      <div className="space-y-3">
        <div className="flex items-center text-green-600">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span className="font-medium">Connected</span>
        </div>
        <p className="text-sm text-gray-600">{status.email}</p>
        <p className="text-xs text-gray-400">Account: {status.accountId}</p>
        <button
          onClick={handleDisconnect}
          className="text-sm text-red-600 hover:text-red-800"
        >
          Disconnect
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        onClick={handleConnect}
        className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
      >
        Connect DocuSign Account
      </button>
      <p className="text-xs text-gray-500">
        You'll be redirected to DocuSign to authorize access.
      </p>
    </div>
  );
}
